import React, { useCallback, useEffect, useRef, useState } from 'react';

import PropTypes from 'prop-types';
import { dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { faPlus } from '@awesome.me/kit-a6e51d954f/icons/classic/regular';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useBoard, useDialogs, useInfiniteScroll } from '../../../hooks';
import { taskStatusShape } from '../../../types';
import Header from './Header';
import TaskCard from './TaskCard';

const ESTIMATED_TASK_CARD_HEIGHT = '100px';

const TASK_CARD_STYLES = {
  containIntrinsicHeight: `auto ${ESTIMATED_TASK_CARD_HEIGHT}`,
  contentVisibility: 'auto',
};

const FadeOverlay = ({ className }) => (
  <div
    className={cn(
      'pointer-events-none absolute w-full z-10 h-2 from-surface-light to-transparent',
      className
    )}
  />
);

FadeOverlay.propTypes = {
  className: PropTypes.string.isRequired,
};

const NewTaskButton = ({ onClick }) => (
  <Button className="bg-white rounded-xl w-full" onClick={onClick} variant="outline">
    <FontAwesomeIcon icon={faPlus} />
  </Button>
);

NewTaskButton.propTypes = {
  onClick: PropTypes.func.isRequired,
};

const StatusGroup = ({ status }) => {
  const ref = useRef(null);

  const [isDraggedOver, setIsDraggedOver] = useState(false);

  const { board, fetchMoreTasks } = useBoard();
  const { handleDeleteTaskClick, handleEditTaskClick, handleNewTaskClick } = useDialogs();

  const {
    tasks,
    pagination: { hasMore, isLoadingMore, totalTasks },
  } = board[status.id];

  const handleLoadMore = useCallback(() => {
    fetchMoreTasks(status.id);
  }, [fetchMoreTasks, status.id]);

  const sentinelRef = useInfiniteScroll({
    hasMore,
    isLoading: isLoadingMore,
    onLoadMore: handleLoadMore,
  });

  const dropTargetStyles = cn(
    'flex flex-col w-[325px] p-2 rounded-sm h-full',
    isDraggedOver ? 'bg-hover' : 'bg-surface-light'
  );

  useEffect(() => {
    const element = ref.current;

    const handleCanDrop = ({ source }) => {
      // Ensure that only TaskCards can be dropped into the StatusGroup.
      return source.data && source.data.type === 'task';
    };

    return dropTargetForElements({
      element: element,
      getData: () => ({ id: status.id }),
      canDrop: handleCanDrop,
      onDragEnter: () => setIsDraggedOver(true),
      onDragLeave: () => setIsDraggedOver(false),
      onDrop: () => setIsDraggedOver(false),
    });
  }, [status.id]);

  return (
    <div className={dropTargetStyles} ref={ref}>
      <Header status={status} taskCount={totalTasks} />

      <div className="relative min-h-0">
        <FadeOverlay className="top-0 bg-gradient-to-b" />

        <div className="h-full pt-2 overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {tasks.map((task) => (
            <div key={task.id} style={TASK_CARD_STYLES}>
              <TaskCard
                onDeleteClick={() => handleDeleteTaskClick(task)}
                onEditClick={() => handleEditTaskClick(task)}
                statusId={task.taskStatus?.id}
                task={task}
              />
            </div>
          ))}

          {hasMore && <div className="h-px" ref={sentinelRef} />}
        </div>

        <FadeOverlay className="bottom-0 bg-gradient-to-t" />
      </div>

      <NewTaskButton onClick={handleNewTaskClick} />
    </div>
  );
};

StatusGroup.propTypes = {
  status: taskStatusShape.isRequired,
};

export default StatusGroup;
